import { mainIconSize } from '@/constants';
import { Org } from '@/types';
import { ReferenceType, UseFloatingOptions } from '@floating-ui/react';
import type { ButtonProps } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { UserPenIcon } from 'lucide-react';
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'usehooks-ts';

import ManageUsers from './ManageUsers';
import styles from './styles.module.css';

const tooltipDelay = 2000;
const hoverOptions = {
  delay: tooltipDelay,
};
// Actions cell renderer component
const ActionsCellRenderer: FC<{ data: Org }> = ({ data }) => {
  const { t } = useTranslation(['orgs']);
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { width } = useWindowSize({ debounceDelay: 150, initializeWithValue: true });

  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  const isFullScreen = useMemo(() => width < 1000, [width]);

  const classNames = useMemo(
    () => ({
      dialog: !isFullScreen ? styles.dialog : undefined,
    }),
    [isFullScreen],
  );

  const floatingProps: Partial<UseFloatingOptions<ReferenceType>> = {
    open: isOpen,
    onOpenChange: setIsOpen,
  };

  const onMouseEnterHandler = useCallback(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => setShowTooltip(true), tooltipDelay);
  }, []);
  const onMouseLeaveHandler = useCallback(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    setShowTooltip(false);
  }, []);

  const triggerProps: Partial<Omit<ButtonProps, 'data-testid'>> = useMemo(
    () => ({
      children: (
        <Tooltip
          content={t('manage_users_tooltip')}
          hoverOptions={hoverOptions}
          isDisabled={isOpen}
          open={showTooltip && !isOpen}
          placement="left"
          trigger={
            <UserPenIcon onMouseEnter={onMouseEnterHandler} onMouseLeave={onMouseLeaveHandler} size={mainIconSize} />
          }
        />
      ),
      size: 'sm',
      visual: 'unstyled',
    }),
    [isOpen, onMouseEnterHandler, onMouseLeaveHandler, showTooltip, t],
  );

  return (
    <Stack
      alignItems="center"
      className={styles.actionButtonContainer}
      gap={2}
      justifyContent="center"
      orientation="horizontal"
    >
      <Dialog
        classNames={classNames}
        content={<ManageUsers org={data} isFullScreen={isFullScreen} />}
        data-testid={`add-user-${data.id}`}
        floatingProps={floatingProps}
        fullScreen={isFullScreen}
        maxWidth={'80%'}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        open={isOpen}
        title={t('orgs:manage_users_title', { name: data.name })}
        triggerProps={triggerProps}
      />
    </Stack>
  );
};

export default memo(ActionsCellRenderer);
